"use client";
import { TechQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/lib/image";
import mainIllu from "@assets/images/tech_illu/main.svg";
import clsx from "clsx";
import Image from "next/image";
import { Dispatch, SetStateAction } from "react";
import dots from "@assets/images/dots.svg";

const Illustration = ({
	hoverItems,
	activeSlideIndex,
	setActiveSlideIndex,
}: {
	hoverItems: NonNullable<TechQueryResult["header"]>["hoverItems"];
	activeSlideIndex: number;
	setActiveSlideIndex: Dispatch<SetStateAction<number>>;
}) => {
	const calculateTranslation = (index: number) => {
		switch (index) {
			case 0:
				return "translate(0, -200%)";
			case 1:
				return "translate(150%, -150%)";
			case 2:
				return "translate(200%, 0)";
			case 3:
				return "translate(150%, 150%)";
			case 4:
				return "translate(0, 200%)";
			case 5:
				return "translate(-150%, 150%)";
			case 6:
				return "translate(-200%, 0)";
			case 7:
				return "translate(-150%, -150%)";
			default:
				return "translate(300%, -150%)";
		}
	};

	const calculateLineRotation = (index: number) => {
		switch (index) {
			case 0:
				return "rotate(90deg)";
			case 1:
				return "rotate(135deg)";
			case 2:
				return "rotate(180deg)";
			case 3:
				return "rotate(225deg)";
			case 4:
				return "rotate(270deg)";
			case 5:
				return "rotate(315deg)";
			case 6:
				return "rotate(0deg)";
			case 7:
				return "rotate(45deg)";
			default:
				return "none";
		}
	};

	const calculateLastIndexOffsetClass = (index: number) => {
		if (index === 8) {
			return "translate-x-[120%] -translate-y-[9rem] sm:-translate-y-[7.5rem] xs:-translate-y-[5.25rem] xs:translate-x-[150%] xxs:translate-x-[180%] duration-500";
		}
		return "";
	};

	return (
		<div className="relative flex w-1/2 items-center justify-center md:w-full md:-translate-x-[5%] sm:min-h-[30rem] sm:-translate-x-[8%] xs:min-h-[22rem]">
			{/* Animation central item */}
			<div className="relative flex aspect-square h-40 items-center justify-center sm:h-32 xs:h-24">
				<Image src={mainIllu.src} fill alt="Main illustration" />
				<div className="rouded-full absolute -z-10 shadow-blue-wide xs:shadow-blue-wide-mobile"></div>
			</div>

			{/* Animation icons */}
			{hoverItems?.map((item, index) => (
				<div
					key={`item-${index}`}
					className="absolute flex cursor-pointer items-end justify-center"
					style={{ transform: calculateTranslation(index) }}
				>
					<div
						className={clsx(
							activeSlideIndex == index
								? index == 8
									? "pulse border-black shadow-blackish"
									: "pulse border-blue shadow-blueish"
								: "border-white",
							"relative flex aspect-square h-24 items-center justify-center rounded-full border bg-white p-3 delay-100 duration-500 sm:h-20 xs:h-14",
						)}
						onClick={() => setActiveSlideIndex(index)}
						onMouseEnter={() => setActiveSlideIndex(index)}
					>
						<div className="relative aspect-square w-[60%] sm:w-[75%] xs:w-[90%]">
							<Image src={urlFor(item.icon).url()} fill alt={item.title?.toLowerCase() || ""} />
						</div>
					</div>
					{item.hasLabel && (
						<div
							className={clsx(
								index !== 8 ? "bg-blue" : "bg-black",
								"absolute whitespace-nowrap rounded-[1.25rem] px-2 font-secondary text-[0.75rem] text-white sm:text-[0.6rem] xs:text-[0.5rem]",
							)}
						>
							{item.label}
						</div>
					)}
				</div>
			))}

			{/* Connecting lines */}
			{hoverItems?.map((_, index) => (
				<div
					key={`line-${index}`}
					style={{ transform: `${calculateLineRotation(index)} translate(-70%,0)` }}
					className={clsx("bg-white", "absolute -z-10 h-[2px] w-[10rem] xs:w-[25%]", calculateLastIndexOffsetClass(index))}
				>
					<div
						className={clsx(
							activeSlideIndex == index ? "pulse-opacity scale-x-100" : "scale-x-0",
							index == 8 ? "bg-black" : "bg-blue",
							"h-[2px] origin-right transition-transform duration-500",
						)}
					/>
				</div>
			))}

			{/* Dots */}
			<div className="absolute left-0 top-0 -z-20 flex h-3/4 w-3/4 items-center justify-center opacity-50">
				<Image src={dots.src} fill alt="Background dots" />
			</div>
		</div>
	);
};

export default Illustration;
